import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import ElipsisCell from "../../../../components/TableBox/Cells/EllipsisCell";
import Badge from "../../../../components/Badge";
import Button from "../../../../components/Button";
import { RowClassName } from "../../../../enums";
import { VALIDATION_FIELD_MAPPING } from "../../../../utils/appConstant";
import { HeadCellType } from "../../../../interfaces/common";
import clsx from "clsx";
import ElipsisText from "../../../../components/ElipsisText";
import { formatDollarAmount } from "../../../../utils/methods";
import { AuthContext } from '../../../../contexts/AuthContext';
import { PermissionModulesEnum, PermissionActionsEnum, Modules } from "../../../../enums";
import { hasResourseAccess } from '../../../../utils/methods';

interface RowProps {
  item: any;
  className?: RowClassName;
  view: boolean;
  headCells: HeadCellType[];
  onEditClick: (data: any) => void;
  onDeletePayment: (paymentId: string) => void;
}

const PaymentRow: React.FC<RowProps> = ({ item, className, view, headCells, onEditClick, onDeletePayment }) => {
  const { t } = useTranslation();
  const { isSystemAdmin, permissions } = useContext(AuthContext);

  const hasEditAccess = hasResourseAccess(isSystemAdmin, permissions, { accessibleTo: [{ [PermissionModulesEnum.Payments]: [PermissionActionsEnum.Update] }] })
  const hasDeleteAccess = hasResourseAccess(isSystemAdmin, permissions, { accessibleTo: [{ [PermissionModulesEnum.Payments]: [PermissionActionsEnum.Delete] }] })

  const handleDeleteClick = () => {
    onDeletePayment(item.id.toString());
  };

  const amountNode = (
    <div className="fw-semibold">
      <span>{item.payment_amount ? formatDollarAmount(item.payment_amount) : '-'}</span>
    </div>
  );

  const actionsNode = (
    <div className="d-flex align-items-sm-center justify-content-sm-start">
      {
        !view && hasEditAccess && <Button
          className="btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"
          icon={<i className="ri-edit-box-line ri-20px"></i>}
          onClick={() => onEditClick(item)}
        />
      }
      <Tooltip
        title={
          <>
            <div>{t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.table.lastUpdatedBy')} {item.updated_by?.name || "-"}</div>
            <div>{t('invoices.editPreviewPayment.pageTexts.form.paymentsTab.table.lastUpdated')} {item.updated_at || "-"}</div>
          </>
        }
        arrow
        classes={{
          tooltip: 'tooltip',
        }}
      >
        <i className="ri-timer-line ri-20px btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill"></i>
      </Tooltip>
      {
        !view && hasDeleteAccess && <Button
          className="btn btn-sm btn-icon btn-text-danger waves-effect waves-light rounded-pill"
          icon={<i className="ri-delete-bin-line ri-20px"></i>}
          onClick={handleDeleteClick}
        />
      }
    </div>
  );

  const isCarryForward = item.payment_type === 'carry_forward';
  const isReceived = item.payment_type === 'received';
  const paymentTypeColor = isCarryForward ? 'warning' : isReceived ? 'primary' : 'success';
  const paymentTypeIcon = isCarryForward ? 'ri-arrow-right-line' : `ri-arrow-${isReceived ? 'down' : 'up'}-line`;
  const paymentTypeLabel = isCarryForward ? 'Forward' : (item.payment_type_name || item.payment_type);

  const paymentTypeNode = (
    <div className="d-flex align-items-center">
      <i className={clsx(`${paymentTypeIcon} ri-16px text-${paymentTypeColor} me-1`)}></i>
      <Badge text={paymentTypeLabel} variant={paymentTypeColor} />
    </div>
  );

  return (
    <tr className={className}>
      {headCells.map((headCell) => {
        if (headCell.id === VALIDATION_FIELD_MAPPING.date) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              text={item.payment_date}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.amount) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={amountNode}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.type) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={paymentTypeNode}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.method) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              text={item.payment_method_name || item.payment_method || "-"}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.account) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              text={item.account?.name || "-"}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.notes) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              text={item.note || "-"}
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.actions) {
          return (
            <td key={headCell.id} width={headCell.width}>
              {actionsNode}
            </td>
          );
        }

        return (
          <ElipsisCell
            key={headCell.id}
            width={headCell.width}
            text={item[headCell.id] ?? "-"}
          />
        );
      })}
    </tr>
  );
};

export default PaymentRow;